import React, { useState, useEffect } from "react";
import { staffAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { isMassageSessionType } from "../../utils/sessionTypeUtils";
import "./BookingHistory.css";

/**
 * BookingHistory component for displaying detailed booking history for a customer
 * Shows booking details, status, and statistics
 */
const BookingHistory = ({ bookings, formatDateTime, customerId }) => {
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("appointmentDate");
  const [sortDirection, setSortDirection] = useState("desc");
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");
  const [bookingStats, setBookingStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState("");

  // Fetch booking statistics when component mounts or customerId changes
  useEffect(() => {
    if (customerId) {
      fetchBookingStats();
    }
  }, [customerId]);

  // Function to fetch booking statistics
  const fetchBookingStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError("");
      const response = await staffAPI.getCustomerBookingStats(customerId);
      setBookingStats(response.data.stats);
      setStatsLoading(false);
    } catch (err) {
      console.error("Error fetching booking statistics:", err);
      setStatsError("Failed to load booking statistics");
      setStatsLoading(false);
      handleApiError(
        err,
        "BookingHistory.stats",
        setStatsError,
        err.response?.data?.message || "Error fetching booking statistics"
      );
    }
  };

  // Calculate total pages based on bookings length (10 per page)
  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(bookings.length / itemsPerPage));

  // Apply filters and sorting to bookings
  const filteredBookings = bookings.filter(
    (booking) =>
      statusFilter === "all" ||
      booking.status.toLowerCase() === statusFilter.toLowerCase()
  );

  // Sort bookings
  const sortedBookings = [...filteredBookings].sort((a, b) => {
    let valueA = a[sortField];
    let valueB = b[sortField];

    // Handle date fields
    if (sortField === "appointmentDate" || sortField === "date") {
      valueA = new Date(
        valueA || a.date || a.startTime || a.createdAt
      ).getTime();
      valueB = new Date(
        valueB || b.date || b.startTime || b.createdAt
      ).getTime();
    }

    // Compare values based on sort direction
    if (sortDirection === "asc") {
      return valueA > valueB ? 1 : -1;
    } else {
      return valueA < valueB ? 1 : -1;
    }
  });

  // Paginate bookings
  const paginatedBookings = sortedBookings.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Format date for display in Phoenix, AZ timezone
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      timeZone: "America/Phoenix",
    });
  };

  // Format time only for display in Phoenix, AZ timezone (12-hour clock)
  const formatTimeOnly = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "America/Phoenix",
    });
  };

  // Handle sort change
  const handleSortChange = (field) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Default to desc for new field
      setSortField(field);
      setSortDirection("desc");
    }
  };

  // Render sort indicator
  const renderSortIndicator = (field) => {
    if (field !== sortField) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  // Render status badge
  const renderStatusBadge = (status) => {
    const statusClass = status.toLowerCase().replace(/[^a-z0-9]/g, "-");
    return (
      <span className={`status-badge status-${statusClass}`}>{status}</span>
    );
  };

  const renderFundingBadge = (booking) => {
    if (booking.sessionSourceType === "organization-sponsored") {
      return (
        <span className="funding-badge funding-sponsored">
          {booking.sourceDescription || "Corporate Sponsored"}
        </span>
      );
    }

    if (booking.sessionSourceType === "organization-corporate-rate") {
      return (
        <div className="funding-cell-copy">
          <span className="funding-badge funding-corporate-rate">
            {booking.sourceDescription || "Corporate Rate"}
          </span>
          <span className="funding-amount">
            ${Number(booking.organizationRateApplied || 0).toFixed(2)}
          </span>
        </div>
      );
    }

    if (booking.sourceDescription) {
      return (
        <span className="funding-badge funding-standard">
          {booking.sourceDescription}
        </span>
      );
    }

    if (booking.sessionSourceType === "paid-service" && isMassageSessionType(booking.sessionType)) {
      return (
        <div className="funding-cell-copy">
          <span className="funding-badge funding-standard">Paid Service</span>
          {booking.paymentAmount ? (
            <span className="funding-amount">
              ${(Number(booking.paymentAmount) / 100).toFixed(2)}
            </span>
          ) : null}
          {booking.refundAmount ? (
            <span className="funding-meta">
              Refunded ${(Number(booking.refundAmount) / 100).toFixed(2)}
            </span>
          ) : booking.paymentStatus ? (
            <span className="funding-meta">
              Payment {String(booking.paymentStatus).replace(/_/g, " ")}
            </span>
          ) : null}
        </div>
      );
    }

    return <span className="funding-badge funding-standard">Standard</span>;
  };

  console.log("Booking Stats:", bookingStats);
  return (
    <div className="booking-history-container">
      {error && <div className="error-message">{error}</div>}

      {/* Booking Statistics */}
      <div className="booking-stats-section">
        <h3>Booking Statistics</h3>
        {statsLoading ? (
          <div className="loading-stats">Loading statistics...</div>
        ) : statsError ? (
          <div className="error-message">{statsError}</div>
        ) : !bookingStats ? (
          <div className="no-stats">No booking statistics available</div>
        ) : (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.totalBookings || 0}
              </div>
              <div className="stat-label">Total Bookings</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.completedBookings || 0}
              </div>
              <div className="stat-label">Completed</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.upcomingBookings || 0}
              </div>
              <div className="stat-label">Upcoming</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.cancelledBookings || 0}
              </div>
              <div className="stat-label">Cancelled</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.saunaBookings || 0}
              </div>
              <div className="stat-label">Sauna Sessions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.redLightBookings || 0}
              </div>
              <div className="stat-label">Red Light Sessions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.massageBookings || 0}
              </div>
              <div className="stat-label">Massage Sessions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.organizationSponsoredBookings || 0}
              </div>
              <div className="stat-label">Corporate Sponsored</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">
                {bookingStats.organizationCorporateRateBookings || 0}
              </div>
              <div className="stat-label">Corporate-Rate Sessions</div>
            </div>
            <div className="stat-card">
              <div className="stat-value text-value">
                $
                {Number(
                  bookingStats.organizationCorporateRateAmount || 0
                ).toFixed(2)}
              </div>
              <div className="stat-label">Corporate-Rate Total</div>
            </div>
            <div className="stat-card">
              <div className="stat-value text-value">
                {bookingStats.mostFrequentDayOfWeek || "N/A"}
              </div>
              <div className="stat-label">Preferred Day</div>
            </div>
            <div className="stat-card">
              <div className="stat-value text-value">
                {bookingStats.mostFrequentTimeOfDay || "N/A"}
              </div>
              <div className="stat-label">Preferred Time</div>
            </div>
          </div>
        )}
      </div>

      {/* Booking Filters */}
      <div className="booking-filters">
        <div className="filter-group">
          <label>Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1); // Reset to first page on filter change
            }}
          >
            <option value="all">All Statuses</option>
            <option value="booked">Booked</option>
            <option value="checked-in">Checked In</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="no-show">No Show</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Sort By:</label>
          <div className="sort-buttons">
            <button
              className={sortField === "appointmentDate" ? "active" : ""}
              onClick={() => handleSortChange("appointmentDate")}
            >
              Date{renderSortIndicator("appointmentDate")}
            </button>
            <button
              className={sortField === "sessionType" ? "active" : ""}
              onClick={() => handleSortChange("sessionType")}
            >
              Type{renderSortIndicator("sessionType")}
            </button>
            <button
              className={sortField === "status" ? "active" : ""}
              onClick={() => handleSortChange("status")}
            >
              Status{renderSortIndicator("status")}
            </button>
          </div>
        </div>
      </div>

      {/* Bookings List */}
      {paginatedBookings.length > 0 ? (
        <div className="bookings-table-container">
          <table className="bookings-table">
            <thead>
              <tr>
                <th onClick={() => handleSortChange("appointmentDate")}>
                  Date/Time{renderSortIndicator("appointmentDate")}
                </th>
                <th onClick={() => handleSortChange("sessionType")}>
                  Session Type{renderSortIndicator("sessionType")}
                </th>
                <th>Suite</th>
                <th onClick={() => handleSortChange("status")}>
                  Status{renderSortIndicator("status")}
                </th>
                <th>Funding</th>
                <th>Duration</th>
                <th>Extras</th>
                <th>Timeline</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBookings.map((booking) => (
                <tr
                  key={booking._id}
                  className={`booking-row status-${booking.status.toLowerCase()}`}
                >
                  <td className="date-cell">
                    <div className="booking-date">
                      {formatDate(booking.date)}
                    </div>
                    <div className="booking-time">
                      {formatTimeOnly(booking.date)}
                    </div>
                  </td>
                  <td>{booking.sessionType}</td>
                  <td>{booking.suite}</td>
                  <td className="status-cell">
                    {renderStatusBadge(booking.status)}
                  </td>
                  <td className="funding-cell">{renderFundingBadge(booking)}</td>
                  <td>{booking.duration} min</td>
                  <td>{booking.extras || "None"}</td>
                  <td className="timeline-cell">
                    <div className="booking-timeline">
                      {booking.statusTimestamps?.booked && (
                        <div className="timeline-item">
                          <div className="timeline-label">Booked:</div>
                          <div className="timeline-value">
                            {formatDateTime(booking.statusTimestamps.booked)}
                          </div>
                        </div>
                      )}
                      {booking.statusTimestamps?.checkedIn && (
                        <div className="timeline-item">
                          <div className="timeline-label">Checked In:</div>
                          <div className="timeline-value">
                            {formatDateTime(booking.statusTimestamps.checkedIn)}
                          </div>
                        </div>
                      )}
                      {booking.statusTimestamps?.started && (
                        <div className="timeline-item">
                          <div className="timeline-label">Started:</div>
                          <div className="timeline-value">
                            {formatDateTime(booking.statusTimestamps.started)}
                          </div>
                        </div>
                      )}
                      {booking.statusTimestamps?.completed && (
                        <div className="timeline-item">
                          <div className="timeline-label">Completed:</div>
                          <div className="timeline-value">
                            {formatDateTime(booking.statusTimestamps.completed)}
                          </div>
                        </div>
                      )}
                      {booking.statusTimestamps?.cancelled && (
                        <div className="timeline-item">
                          <div className="timeline-label">Cancelled:</div>
                          <div className="timeline-value">
                            {formatDateTime(booking.statusTimestamps.cancelled)}
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-bookings">
          <p>No booking history found for the selected filters.</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span className="page-info">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default BookingHistory;
